import React, { useState } from 'react';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Icon, Chip, Checkbox, IconButton, useTheme,
} from '@mui/material';
import { Text } from 'global/components/text';
import { Button } from 'global/components/Button';
import Modal from 'global/components/modal';
import { FIELD_CATEGORIES, getCategoryByComponent, getDefaultFieldConfig } from './constants';
import TaskTypeFieldConfig from './TaskTypeFieldConfig';
import { get, size } from 'global/lib/lodash';
import {
  ActionIcons,
  AddFieldBtn,
  DragHandle,
  FieldPickerGrid,
  FieldsHeaderCell,
  FieldsRow,
  Section,
  TypeLabel,
  TypeLabelDisabled,
} from './styles';

const isDefault = (field) => field.id.startsWith('f_');

const TEXT_SUB_TYPE_LABELS = { TextField: 'TEXT', LongText: 'LONG TEXT', RichText: 'RICH TEXT' };

const getTypeLabel = (field) => {
  if (field.type_label) return field.type_label;
  const catEntry = getCategoryByComponent(field.field_type);
  if (catEntry && catEntry[0] === 'Text') {
    const subType = get(field, 'config.sub_type', field.field_type);
    return TEXT_SUB_TYPE_LABELS[subType] || catEntry[1].label.toUpperCase();
  }
  return catEntry ? catEntry[1].label.toUpperCase() : (field.field_type || 'UNKNOWN').toUpperCase();
};

const SortableFieldRow = ({ field, index, canEdit, columns, colActions }) => {
  const theme = useTheme();
  const disabled = !canEdit || !field.is_enabled;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? theme.palette.action.hover : undefined,
  };
  return (
    <FieldsRow ref={setNodeRef} style={style} {...attributes}>
      <TableCell style={{ width: 40 }}>
        <DragHandle
          {...(disabled ? {} : listeners)}
          style={{
            ...(disabled ? { opacity: 0.38, pointerEvents: 'none' } : { cursor: 'grab', touchAction: 'none' }),
          }}>
          <Icon fontSize="small">drag_indicator</Icon>
        </DragHandle>
      </TableCell>
      {columns.map((col) => (
        <TableCell key={col.field} align={col.align} style={col.width ? { width: col.width } : undefined}>
          {col.render(field, index, colActions)}
        </TableCell>
      ))}
    </FieldsRow>
  );
};

// --- Column definitions ---
// Each column: { field, title, align, width, render(field, index, actions) }
// `actions` provides: { toggleEnabled, toggleRequired, openConfig, removeField, classes }
const COLUMNS = [
  {
    field: 'label',
    title: 'Field Name',
    render: (field) => `${field.label || '(untitled)'}${isDefault(field) ? ' *' : ''}`,
  },
  {
    field: 'is_enabled',
    title: 'Enabled',
    align: 'center',
    render: (field, index, { toggleEnabled }) => (
      <Checkbox
        checked={field.is_enabled}
        disabled={field.locked}
        onChange={() => toggleEnabled(index)}
        size="small"
        color="primary"
      />
    ),
  },
  {
    field: 'is_required',
    title: 'Required**',
    align: 'center',
    render: (field, index, { toggleRequired }) => (
      <Checkbox
        checked={field.is_required}
        disabled={field.locked || !field.is_enabled}
        onChange={() => toggleRequired(index)}
        size="small"
        color="primary"
      />
    ),
  },
  {
    field: 'field_type',
    title: 'Field Type',
    align: 'center',
    render: (field) => {
      const Label = field.is_enabled ? TypeLabel : TypeLabelDisabled;
      return <Label>{getTypeLabel(field)}</Label>;
    },
  },
  {
    field: 'actions',
    title: '',
    width: 80,
    render: (field, index, { openConfig, confirmRemoveField }) => {
      const def = isDefault(field);
      const disabled = def || !field.is_enabled;
      return (
        <ActionIcons>
          <IconButton size="small" disabled={disabled} onClick={() => openConfig(field, index)}>
            <Icon fontSize="small">settings</Icon>
          </IconButton>
          <IconButton size="small" disabled={disabled} onClick={() => confirmRemoveField(field, index)}>
            <Icon fontSize="small">delete</Icon>
          </IconButton>
        </ActionIcons>
      );
    },
  },
];

const TaskTypeFieldBuilder = ({ fields, onChange, canEdit }) => {
  const [configModal, setConfigModal] = useState({ open: false, field: null, index: -1 });
  const [addModal, setAddModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, index: -1, name: '' });

  const addField = (categoryKey) => {
    const cat = FIELD_CATEGORIES[categoryKey];
    const newField = {
      id: `cf_${Date.now()}`,
      field_type: cat.component,
      label: '',
      is_required: false,
      is_enabled: true,
      order: size(fields),
      config: getDefaultFieldConfig(categoryKey),
    };
    setAddModal(false);
    setConfigModal({ open: true, field: newField, index: -1 });
  };

  const updateField = (field, index) => {
    if (index === -1) {
      const processed = processFieldOptions(field);
      onChange([...fields, { ...processed, order: size(fields) }]);
    } else {
      const processed = processFieldOptions(field);
      const updated = [...fields];
      updated[index] = processed;
      onChange(updated);
    }
    setConfigModal({ open: false, field: null, index: -1 });
  };

  const removeField = (index) => {
    const updated = [...fields];
    updated.splice(index, 1);
    onChange(updated.map((f, i) => ({ ...f, order: i })));
  };

  const toggleEnabled = (index) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], is_enabled: !updated[index].is_enabled };
    onChange(updated);
  };

  const toggleRequired = (index) => {
    const updated = [...fields];
    updated[index] = { ...updated[index], is_required: !updated[index].is_required };
    onChange(updated);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(fields, oldIndex, newIndex);
    onChange(reordered.map((f, i) => ({ ...f, order: i })));
  };

  const openConfig = (field, index) => setConfigModal({ open: true, field, index });
  const confirmRemoveField = (field, index) => setDeleteConfirm({ open: true, index, name: field.label || '(untitled)' });
  const colActions = { toggleEnabled, toggleRequired, openConfig, confirmRemoveField };

  return (
    <Section>
      <Text type="title_medium" bold sx={{ mb: 1 }}>
        Fields
      </Text>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <FieldsHeaderCell style={{ width: 40 }} />
                {COLUMNS.map((col) => (
                  <FieldsHeaderCell
                    key={col.field}
                    align={col.align}
                    style={col.width ? { width: col.width } : undefined}>
                    {col.title}
                  </FieldsHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
              <TableBody>
                {fields.map((field, index) => (
                  <SortableFieldRow
                    key={field.id}
                    field={field}
                    index={index}
                    canEdit={canEdit}
                    columns={COLUMNS}
                    colActions={colActions}
                  />
                ))}
              </TableBody>
            </SortableContext>
          </Table>
        </TableContainer>
      </DndContext>

      {canEdit && (
        <AddFieldBtn as={Button}
          variant="contained"
          color="primary"
          startIcon={<Icon>add</Icon>}
          onClick={() => setAddModal(true)}
        >
          ADD NEW FIELD
        </AddFieldBtn>
      )}

      <Text type="body_small" color="text.secondary" italic sx={{ mt: 1 }}>
        * Default fields can't be modified, only disabled and/or required
      </Text>
      <Text type="body_small" color="text.secondary" italic sx={{ mt: 1 }}>
        ** Required fields are those required to create or save a task
      </Text>

      {/* Field Type Picker */}
      <Modal
        open={addModal}
        onClose={() => setAddModal(false)}
        title="Add Custom Field"
        minWidth={350}
      >
        <FieldPickerGrid>
          {Object.entries(FIELD_CATEGORIES).map(([key, cat]) => (
            <Chip
              key={key}
              icon={<Icon>{cat.icon}</Icon>}
              label={cat.label}
              onClick={() => addField(key)}
              clickable
              variant="outlined"
            />
          ))}
        </FieldPickerGrid>
      </Modal>

      {/* Field Config Modal */}
      {configModal.open && (
        <TaskTypeFieldConfig
          open={configModal.open}
          field={configModal.field}
          onSave={(f) => updateField(f, configModal.index)}
          onClose={() => setConfigModal({ open: false, field: null, index: -1 })}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, index: -1, name: '' })}
        title="Delete Field"
        buttons={[
          { title: 'Cancel', onClick: () => setDeleteConfirm({ open: false, index: -1, name: '' }), variant: 'outlined' },
          {
            title: 'Delete',
            onClick: () => {
              removeField(deleteConfirm.index);
              setDeleteConfirm({ open: false, index: -1, name: '' });
            },
            variant: 'contained',
          },
        ]}
      >
        {`Are you sure you want to delete the field "${deleteConfirm.name}"?`}
      </Modal>
    </Section>
  );
};

// Convert ChipAdd string arrays back to proper option objects
function processFieldOptions(field) {
  const processed = { ...field, config: { ...field.config } };

  if (field.field_type === 'ToggleButton' && Array.isArray(get(field, 'config.options'))) {
    const opts = field.config.options;
    if (opts.length > 0 && typeof opts[0] === 'string') {
      processed.config.options = opts.map((o) => ({ title: o, value: o.toLowerCase().replace(/\s+/g, '_') }));
    }
  }

  if (field.field_type === 'Select' && Array.isArray(get(field, 'config.options'))) {
    const opts = field.config.options;
    if (opts.length > 0 && typeof opts[0] === 'string') {
      processed.config.options = opts.map((o) => ({ label: o, value: o.toLowerCase().replace(/\s+/g, '_') }));
    }
  }

  return processed;
}

export default TaskTypeFieldBuilder;
